export { Page as default }

import { useEffect, useState } from 'react'
import { useData } from 'vike-react/useData'
import { reload } from 'vike/client/router'
import { errorStyle, hintStyle, sepStyle } from '../../components/messageStyles.ts'
import { Login } from './Login.tsx'
import { Logo } from '../../components/Logo.tsx'
import { Footer } from '../../components/Footer.tsx'
import { useTelefuncAbort } from '../../components/useTelefuncAbort.ts'
import { logoutAndReload } from '../../components/logoutAndReload.ts'
import { hasLoginHash } from './loginHash.ts'
import { EmailPanel } from './EmailPanel.tsx'
import { LicenseKeysPanel } from './LicenseKeysPanel.tsx'
import type { LicenseMap } from './LicenseKeysPanel.tsx'
import type { Data } from './+data.client.ts'

// The sign-in bootstrap (consume the `#login:` fragment → set the HttpOnly cookie) AND the
// dashboard load both run in `+data.client.ts` before the first render. So `useData()` is
// already resolved here: no in-component loading state, no flash of <Login> while the
// cookie is being set, and — because `data()` is a Vike routing hook rather than a React
// effect — no StrictMode double-fetch. `onLoadDashboard` is still the source of truth for
// "is this customer signed in?"; the client never inspects the cookie itself.
function Page() {
  const data = useData<Data>()
  useTelefuncAbort()
  useReloadOnLoginHash()
  useStripConsumedLoginHash()

  if ('error' in data) {
    return (
      <main style={style.page}>
        <p style={style.error}>{data.error}</p>
      </main>
    )
  }
  if (!data.loggedIn) return <Login linkExpired={data.linkExpired} />
  return <Dashboard email={data.email} license={data.license} />
}

// Pasting a fresh magic link into an already-open tab fires `hashchange`, not a remount, and Vike
// doesn't re-run `data()` for a fragment-only change. Trigger `reload()` so `data()` re-runs and
// `consumeLoginHash` → `readLoginToken` exchanges the freshly-pasted token — which `readLoginToken`
// reads straight from the live URL, since a soft `reload()` does NOT re-run `+client`'s
// `captureLoginToken` (so the module-level captured value can be stale here). Reads the LIVE hash.
function useReloadOnLoginHash(): void {
  useEffect(() => {
    const onHash = () => {
      if (hasLoginHash()) void reload()
    }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
}

// Backstop strip. The fragment is normally captured + stripped at boot by `captureLoginToken`
// (`+client.ts`), so this is usually a no-op. It stays as a safety net in case Vike re-applies the
// navigation's original URL after `data()` resolves (it re-applies the URL, which would restore
// the fragment) — stripping here, after render, sticks. No deps (runs after every render): cheap
// when there's no `#login:` fragment. `replaceState` emits no `hashchange`, so this can't loop
// with `useReloadOnLoginHash`.
function useStripConsumedLoginHash(): void {
  useEffect(() => {
    if (hasLoginHash()) {
      history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  })
}

// Local view state seeded from the loaded data — the panels push updates back after a key
// issuance / email change, so the snippet + identity line refresh without a reload.
function Dashboard({ email: initialEmail, license: initialLicense }: { email: string; license: LicenseMap }) {
  const [email, setEmail] = useState(initialEmail)
  const [license, setLicense] = useState(initialLicense)

  return (
    <main style={style.page}>
      <header style={style.header}>
        <div style={style.brand}>
          <a href="https://vike.dev">
            <Logo size={30} />
          </a>
          <h1 style={style.h1}>Dashboard</h1>
        </div>
        <p style={style.identity}>
          Signed in as <strong>{email}</strong>
          <span style={sepStyle}>·</span>
          <button type="button" onClick={logoutAndReload} style={style.signOutButton}>
            Sign out
          </button>
        </p>
      </header>

      <LicenseKeysPanel license={license} onUpdate={setLicense} />

      <EmailPanel currentEmail={email} onUpdate={setEmail} />

      <Footer />
    </main>
  )
}

const style = {
  page: { fontFamily: 'system-ui, sans-serif', maxWidth: 720, padding: 24, margin: '0 auto' },
  header: { marginBottom: 24 },
  brand: { display: 'flex', alignItems: 'center', gap: 10 },
  h1: { margin: 0, fontSize: 24 },
  error: errorStyle,
  identity: { ...hintStyle, marginTop: 4 },
  signOutButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: '#555',
    cursor: 'pointer',
    textDecoration: 'underline',
    font: 'inherit',
  },
}
